/**
 * React Query hooks for Ideas.
 *
 * Data fetching and mutations resolve the active ``HttpClient`` via
 * ``useHttpClient()`` so the same hooks power both admin and portal trees.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useHttpClient } from "@/core/http/HttpClientContext";
import { ideasService } from "../services/ideasService";
import {
  Idea,
  IdeaStatus,
  CreateIdeaRequest,
  UpdateIdeaRequest,
  ideaKeys,
} from "../types";

/**
 * Fetches ideas for the active HttpClient context. Admin context builds
 * ``/api/clients/{clientId}/ideas``; portal context builds
 * ``/api/client-portal/ideas``. Client scoping for portal comes from the JWT.
 */
export function useIdeasQuery(
  clientId: string | undefined,
  status?: IdeaStatus | string,
  includePosts = false,
  includeLinks = false,
) {
  const http = useHttpClient();
  return useQuery({
    queryKey: clientId
      ? [...ideaKeys.list(clientId, status as IdeaStatus | undefined), { includePosts, includeLinks, ctx: http.context.kind }]
      : ["ideas", "none"],
    queryFn: async () => {
      if (!clientId) return [];
      return ideasService.getIdeas(http, status, includePosts, includeLinks);
    },
    enabled: !!clientId,
  });
}

export function useIdeaQuery(
  ideaId: string | undefined,
  includeNotes = false,
  includePosts = false,
  includeLinks = false,
) {
  const http = useHttpClient();
  return useQuery({
    queryKey: ideaId
      ? [...ideaKeys.detail(ideaId), { includeNotes, includePosts, includeLinks }]
      : ["ideas", "none"],
    queryFn: async () => {
      if (!ideaId) return null;
      return ideasService.getIdea(http, ideaId, includeNotes, includePosts, includeLinks);
    },
    enabled: !!ideaId,
  });
}

export function useApprovedIdeasQuery(clientId: string | undefined) {
  const http = useHttpClient();
  return useQuery({
    queryKey: clientId ? ideaKeys.approved(clientId) : ["ideas", "none"],
    queryFn: async () => {
      if (!clientId) return [];
      return ideasService.getApprovedIdeas(http, clientId);
    },
    enabled: !!clientId,
  });
}

export function useCreateIdeaMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateIdeaRequest) =>
      ideasService.createIdea(http, clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ideaKeys.lists() });
    },
  });
}

export function useUpdateIdeaMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ideaId,
      data,
    }: {
      ideaId: string;
      data: UpdateIdeaRequest;
    }) => ideasService.updateIdea(http, ideaId, data),
    onMutate: async ({ ideaId, data }) => {
      await queryClient.cancelQueries({ queryKey: ideaKeys.lists() });
      const listQueries = queryClient.getQueriesData<Idea[]>({ queryKey: ideaKeys.lists() });
      queryClient.setQueriesData<Idea[]>({ queryKey: ideaKeys.lists() }, (old) =>
        old?.map((idea) => {
          if (idea.id !== ideaId) return idea;
          const merged = { ...idea, ...data };
          if (data.body) merged.body = { ...idea.body, ...data.body };
          return merged as Idea;
        })
      );
      const detailQueries = queryClient.getQueriesData<Idea | null>({ queryKey: ideaKeys.detail(ideaId) });
      queryClient.setQueriesData<Idea | null>({ queryKey: ideaKeys.detail(ideaId) }, (old) => {
        if (!old) return old;
        const merged = { ...old, ...data };
        if (data.body) merged.body = { ...old.body, ...data.body };
        return merged as Idea;
      });
      return { listQueries, detailQueries };
    },
    onError: (_err, _variables, context) => {
      if (context?.listQueries) {
        for (const [key, data] of context.listQueries) queryClient.setQueryData(key, data);
      }
      if (context?.detailQueries) {
        for (const [key, data] of context.detailQueries) queryClient.setQueryData(key, data);
      }
    },
    onSettled: (_data, _error, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
      queryClient.invalidateQueries({ queryKey: ideaKeys.lists() });
    },
  });
}

export function useDeleteIdeaMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ideaId: string) => {
      await ideasService.deleteIdea(http, ideaId);
      return ideaId;
    },
    onMutate: async (ideaId) => {
      await queryClient.cancelQueries({ queryKey: ideaKeys.list(clientId) });
      const previousIdeas = queryClient.getQueryData<Idea[]>(ideaKeys.list(clientId));
      if (previousIdeas) {
        queryClient.setQueryData<Idea[]>(ideaKeys.list(clientId), (old) =>
          old?.filter((idea) => idea.id !== ideaId)
        );
      }
      return { previousIdeas };
    },
    onError: (_err, _ideaId, context) => {
      if (context?.previousIdeas) {
        queryClient.setQueryData(ideaKeys.list(clientId), context.previousIdeas);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ideaKeys.lists() });
    },
  });
}

export function useSubmitIdeaMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ideaId: string) => ideasService.submitForApproval(http, ideaId),
    onSuccess: (updatedIdea) => {
      queryClient.setQueryData(ideaKeys.detail(updatedIdea.id), updatedIdea);
      queryClient.invalidateQueries({ queryKey: ideaKeys.lists() });
    },
  });
}

export function useRevertIdeaMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ideaId: string) => ideasService.revertToDraft(http, ideaId),
    onSuccess: (updatedIdea) => {
      queryClient.setQueryData(ideaKeys.detail(updatedIdea.id), updatedIdea);
      queryClient.invalidateQueries({ queryKey: ideaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ideaKeys.approved(clientId) });
    },
  });
}

export function useLinkIdeaNoteMutation() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ideaId, noteId }: { ideaId: string; noteId: string }) => {
      await ideasService.linkNote(http, ideaId, noteId);
    },
    onSuccess: (_data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
    },
  });
}

export function useUnlinkIdeaNoteMutation() {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ideaId, noteId }: { ideaId: string; noteId: string }) => {
      await ideasService.unlinkNote(http, ideaId, noteId);
    },
    onSuccess: (_data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
    },
  });
}

export function useDuplicateIdeaMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ideaId: string) => ideasService.duplicateIdea(http, ideaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ideaKeys.lists() });
    },
  });
}

export function useSetIdeaBriefMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      ideaId,
      briefId,
    }: {
      ideaId: string;
      briefId: string | null;
    }) => ideasService.setBrief(http, ideaId, briefId),
    onSuccess: (updatedIdea) => {
      queryClient.setQueryData(ideaKeys.detail(updatedIdea.id), updatedIdea);
      queryClient.invalidateQueries({ queryKey: ideaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["briefs"] });
    },
  });
}

export const ideaCacheUtils = {
  upsertIdea(clientId: string, idea: Idea, queryClient: ReturnType<typeof useQueryClient>): void {
    queryClient.setQueryData<Idea[]>(ideaKeys.list(clientId), (old) => {
      if (!old) return [idea];
      const exists = old.some((i) => i.id === idea.id);
      if (exists) return old.map((i) => (i.id === idea.id ? idea : i));
      return [idea, ...old];
    });
    queryClient.setQueryData(ideaKeys.detail(idea.id), idea);
  },

  removeIdea(clientId: string, ideaId: string, queryClient: ReturnType<typeof useQueryClient>): void {
    queryClient.setQueryData<Idea[]>(ideaKeys.list(clientId), (old) =>
      old?.filter((i) => i.id !== ideaId)
    );
    queryClient.removeQueries({ queryKey: ideaKeys.detail(ideaId) });
  },

  invalidateAll(clientId: string, queryClient: ReturnType<typeof useQueryClient>): void {
    queryClient.invalidateQueries({ queryKey: ideaKeys.list(clientId) });
    queryClient.invalidateQueries({ queryKey: ideaKeys.approved(clientId) });
  },
};
